import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Project } from 'src/app/models/project';
import { logError } from '../helpers/sentry.helper';
import { ProjectService } from '../project.service';
import { HbmConfigurator } from './hbm-configurator';
import { HbmConfiguratorRegistry } from './hbm-configurator-registry.service';

const HTTP_NOT_FOUND = 404;

/** Duplique un projet HomeByMe (Cuisine, Rangement, Salle de bain) et son tunnel Castorama pour le client courant. */
@Injectable({ providedIn: 'root' })
export class HbmProjectDuplicationService {
  private readonly projectService = inject(ProjectService);
  private readonly registry = inject(HbmConfiguratorRegistry);

  /** Indique si le projet est un projet HomeByMe (Cuisine, Rangement ou Salle de bain). */
  isHbmProject(project: Project): boolean {
    return this.registry.byDistributionID(project.distributionID) !== undefined;
  }

  /**
   * Duplique le projet HomeByMe puis son tunnel, et retourne le projet cloné.
   * Le projet HomeByMe cloné est supprimé si la duplication du tunnel échoue.
   */
  async duplicate(project: Project, newName: string): Promise<Project> {
    const configurator = this.registry.byDistributionID(project.distributionID);
    const sourceTunnel = await this.findSourceTunnel(configurator, project.projectUUID);
    const clonedUuid = await this.projectService.duplicate(project.projectID, project.projectUUID, newName);

    try {
      await this.duplicateTunnel(configurator, sourceTunnel, clonedUuid, newName);
    } catch (error) {
      await this.deleteClone(clonedUuid);
      throw error;
    }

    return Object.assign(new Project(), {
      projectUUID: clonedUuid,
      projectName: newName,
      distributionID: project.distributionID,
    });
  }

  private async duplicateTunnel(
    configurator: HbmConfigurator | undefined,
    sourceTunnel: { id?: string } | undefined,
    clonedUuid: string,
    title: string
  ): Promise<void> {
    if (!configurator || !sourceTunnel?.id) {
      return;
    }

    await configurator.duplicateTunnel({
      hbmConfigurationId: clonedUuid,
      tunnelConfigurationId: sourceTunnel.id,
      newTunnelTitle: title,
    });
  }

  private async deleteClone(clonedUuid: string): Promise<void> {
    try {
      const clone = await this.projectService.getSummary(clonedUuid.toUpperCase());
      await this.projectService.delete(clone.projectID, false);
    } catch (error) {
      logError(error);
    }
  }

  private async findSourceTunnel(
    configurator: HbmConfigurator | undefined,
    hbmUuid: string
  ): Promise<{ id?: string } | undefined> {
    if (!configurator) {
      return undefined;
    }

    try {
      return await configurator.getTunnel(hbmUuid);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === HTTP_NOT_FOUND) {
        return undefined;
      }
      throw error;
    }
  }
}
